import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";
import { historyData } from "@/data/mockData";
import { streakService } from "./streakService";
import { handleSupabaseError } from "@/utils/duplicateUtils";
import { 
  getCleanSectionName, 
  isWritingCompetency, 
  isFullWritingSection 
} from "@/utils/questionSectionUtils";
import { getTefLevelByScore } from "@/data/tefScoreScale";
import { normalizeWritingCorrection } from "@/utils/writingResultUtils";
import { getCurrentUserIdOrThrow } from "@/services/authService";
import { profileService } from "./profileService";
import { activityService } from "./activityService";



export function parseNumericValue(value: unknown): number | null {
  if (value === null || value === undefined) return null;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (!trimmed || trimmed === "--" || trimmed.toLowerCase() === "n/a") {
      return null;
    }

    // Caso "7/7", "300/699", "300 / 699"
    if (trimmed.includes("/")) {
      const firstPart = trimmed.split("/")[0].trim();
      const parsed = Number(firstPart.replace(",", "."));
      return Number.isFinite(parsed) ? parsed : null;
    }

    // Caso "100%"
    const cleaned = trimmed
      .replace("%", "")
      .replace("pts", "")
      .replace(",", ".")
      .trim();

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function parseIntegerValue(value: unknown): number | null {
  const parsed = parseNumericValue(value);
  return parsed === null ? null : Math.round(parsed);
}

export interface HistoryItem {
  id: string;
  date: string;
  competence: "Reading" | "Listening" | "Writing" | string;
  score: string;
  timeSpent: string;
  status: string;
  words?: number;
  mode?: "single" | "section" | "complete" | "full_exam";
  selectedSection?: string;
  sectionResults?: Record<string, { correct: number; total: number; percent: number }>;
  percentage?: number;
  text?: string;
  sections?: {
    reading?: {
      total: number;
      correct: number;
      percent: number;
      sectionResults?: Record<string, { correct: number; total: number; percent: number }>;
    };
    listening?: {
      total: number;
      correct: number;
      percent: number;
      sectionResults?: Record<string, { correct: number; total: number; percent: number }>;
    };
    writing?: {
      text: string;
      words: number;
    };
  };
  metadata?: {
    testAttemptId?: string;
    [key: string]: any;
  };
  test_type?: "single" | "section" | "complete" | "full_exam" | string;
  testType?: "single" | "section" | "complete" | "full_exam" | string;
  result?: any;
  attempt_key?: string;
  attemptKey?: string;
  isWritingCorrection?: boolean;
  estimatedScore?: number;
  maxScore?: number;
  nclcEstimate?: string;
  cecrlEstimate?: string;
  writingCorrection?: any;
  aiCorrection?: any;
  correction?: any;
}

const STORAGE_KEY = "tef_test_history";

function parseTimeToSeconds(timeSpent?: string): number {
  if (!timeSpent) return 0;
  if (timeSpent.includes(":")) {
    const parts = timeSpent.split(":");
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  }
  const mMatch = timeSpent.match(/(\d+)m/);
  const sMatch = timeSpent.match(/(\d+)s/);
  const mins = mMatch ? parseInt(mMatch[1]) : 0;
  const secs = sMatch ? parseInt(sMatch[1]) : 0;
  return mins * 60 + secs;
}

export function mapSupabaseToHistoryItem(row: any): HistoryItem {
  const base = row.result || {};
  return {
    date: row.created_at || base.date || new Date().toISOString(),
    competence: row.competence || base.competence || "Reading",
    score: row.score || base.score || "N/A",
    timeSpent: base.timeSpent || (row.duration_seconds ? `${Math.floor(row.duration_seconds / 60)}m ${row.duration_seconds % 60}s` : "N/A"),
    status: base.status || "Concluído",
    words: base.words || row.result?.words,
    mode: row.test_type as any || base.mode || "single",
    selectedSection: row.section || base.selectedSection,
    sectionResults: base.sectionResults,
    percentage: row.percentage || base.percentage,
    text: base.text || row.result?.text,
    sections: base.sections,
    metadata: row.metadata || base.metadata || {},
    test_type: row.test_type || base.test_type || base.mode,
    testType: row.test_type || base.testType || base.mode,
    attempt_key: row.attempt_key || base.attempt_key || base.metadata?.testAttemptId,
    attemptKey: row.attempt_key || base.attemptKey || base.metadata?.testAttemptId,
    isWritingCorrection: row.isWritingCorrection || base.isWritingCorrection,
    estimatedScore: row.estimatedScore || base.estimatedScore || row.score_points,
    maxScore: row.max_score || base.maxScore || row.max_score_points,
    nclcEstimate: row.nclc_level || base.nclcEstimate,
    cecrlEstimate: row.cefr_level || base.cecrlEstimate,
    ...base,
    id: row.id // enforce remote primary key
  };
}

export function mapHistoryToSupabase(item: any): any {
  let totalQ = 0;
  let correctQ = 0;
  
  const isComplete = item.testType === "complete" || item.test_type === "complete" || item.mode === "full_exam" || item.competence === "Simulado Completo" || isFullWritingSection(item.section);

  if (isComplete) {
    const ce = item.result?.ce || item.sections?.reading || {};
    const co = item.result?.co || item.sections?.listening || {};
    totalQ = (ce.total || ce.totalQuestions || 0) + (co.total || co.totalQuestions || 0);
    correctQ = (ce.correct || ce.correctAnswers || 0) + (co.correct || co.correctAnswers || 0);
  } else if (item.sectionResults) {
    Object.values(item.sectionResults).forEach((res: any) => {
      totalQ += res.total || 0;
      correctQ += res.correct || 0;
    });
  }

  if (!totalQ && item.score && typeof item.score === "string" && item.score.includes("/")) {
    const match = item.score.match(/(\d+)\/(\d+)/);
    if (match) {
      correctQ = parseInt(match[1]);
      totalQ = parseInt(match[2]);
    }
  }

  const duration = item.timeUsed || item.durationSeconds || item.duration_seconds || parseTimeToSeconds(item.timeSpent);
  const testType = isComplete ? "complete" : (item.mode || item.testType || item.test_type || "single");

  // Build sanitized, correct typed DB object
  const rawPayload = {
    user_id: item.user_id || item.userId || null,
    test_type: testType,
    competence: isComplete ? "Simulado Completo" : (item.competence || "Reading"),
    section: isComplete ? "Completo" : (item.selectedSection || null),
    
    // Type precise values utilizing parseNumericValue and parseIntegerValue
    score: parseNumericValue(item.score ?? item.result?.score ?? correctQ) || 0,
    max_score: parseNumericValue(item.maxScore ?? item.max_score ?? totalQ) || 0,
    percentage: parseNumericValue(item.percentage ?? (totalQ ? Math.round((correctQ / totalQ) * 100) : null)) || 0,
    
    nclc_level: item.metadata?.nclcLevel || item.nclcEstimate || item.nclcLevel || item.nclc_level || (isWritingCompetency(item.competence) ? getTefLevelByScore("ee", item.score ?? item.result?.score ?? correctQ)?.nclc : null),
    cefr_level: item.metadata?.cefrLevel || item.cecrlEstimate || item.cefrLevel || item.cefr_level || (isWritingCompetency(item.competence) ? getTefLevelByScore("ee", item.score ?? item.result?.score ?? correctQ)?.cecrl : null),
    
    total_questions: parseIntegerValue(item.totalQuestions ?? item.total_questions ?? totalQ) || 0,
    correct_answers: parseIntegerValue(item.correctAnswers ?? item.correct_answers ?? correctQ) || 0,
    duration_seconds: parseIntegerValue(duration) || 0,
    
    answers: item.answers || null,
    result: item, // Serialize entire local object for 100% recovery
    metadata: item.metadata || {},
    attempt_key: item.attempt_key || item.attemptKey || item.metadata?.testAttemptId || null
  };

  const sanitizeJson = (val: any) => {
    if (val === undefined || val === null) return {};
    return val;
  };

  const sanitizeString = (val: any) => {
    if (val === undefined || val === null) return null;
    return String(val);
  };

  return {
    user_id: sanitizeString(rawPayload.user_id),
    test_type: sanitizeString(rawPayload.test_type),
    competence: sanitizeString(rawPayload.competence),
    section: sanitizeString(rawPayload.section),
    score: rawPayload.score,
    max_score: rawPayload.max_score,
    percentage: rawPayload.percentage,
    nclc_level: sanitizeString(rawPayload.nclc_level),
    cefr_level: sanitizeString(rawPayload.cefr_level),
    total_questions: rawPayload.total_questions,
    correct_answers: rawPayload.correct_answers,
    duration_seconds: rawPayload.duration_seconds,
    answers: sanitizeJson(rawPayload.answers),
    result: sanitizeJson(rawPayload.result),
    metadata: {
      ...sanitizeJson(rawPayload.metadata),
      status: sanitizeString(item.status)
    },
    attempt_key: sanitizeString(rawPayload.attempt_key) || `h-${Date.now()}`
  };
}

export function sanitizeTestHistoryPayload(payload: any) {
  const cleaned = { ...payload };
  delete cleaned.status;
  return cleaned;
}

export const historyService = {
  // Broadcaster
  emitHistoryUpdated() {
    window.dispatchEvent(new Event("historyUpdated"));
    window.dispatchEvent(new Event("dashboardUpdated"));
  },

  async saveCompletedWritingHistory({
    correctionResult,
    writingTasks,
    section,
    timeUsed,
    wordCount,
  }: {
    correctionResult: any;
    writingTasks?: any[];
    section?: string;
    timeUsed?: number | string;
    wordCount?: number;
  }) {
    const normalized = normalizeWritingCorrection(correctionResult);

    if (!normalized.totalScore || normalized.totalScore <= 0) {
      console.error("[EE_SAVE_COMPLETED_ABORTED_INVALID_SCORE]", normalized);
      throw new Error("Nota de EE inválida. Histórico não será salvo sem pontuação.");
    }

    const metadata = {
      isWritingTest: true,
      isFullWritingTest:
        String(section || "").toLowerCase().includes("completo") ||
        (Array.isArray(writingTasks) && writingTasks.length >= 2),

      writingCorrection: normalized,
      writingTasks: writingTasks || [],
      sectionScores: normalized.sectionScores || {
        sectionA: normalized.sectionA || null,
        sectionB: normalized.sectionB || null,
        total: {
          score: normalized.totalScore,
          maxScore: normalized.maxScore || 450,
        },
      },

      totalScore: normalized.totalScore,
      maxScore: normalized.maxScore || 450,
      nclc: normalized.nclc,
      cefr: normalized.cefr,
      wordCount: wordCount || normalized.wordCount || null,
      correctionSource: normalized.source || "ai",
      status: "concluido",
      correctionSavedAt: new Date().toISOString(),
    };

    const payload = {
      competence: "Expression Écrite (EE)",
      section: section || "Completo",
      score: normalized.totalScore,
      max_score: normalized.maxScore || 450,
      nclc_level: normalized.nclc,
      cefr_level: normalized.cefr,
      duration_seconds: typeof timeUsed === "number" ? timeUsed : null,
      metadata,
    };

    // Authenticate user
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      throw new Error("Usuário não autenticado. Não é possível salvar histórico.");
    }

    (payload as any).user_id = userData.user.id;

    console.log("[EE_SAVE_COMPLETED_HISTORY_PAYLOAD]", JSON.stringify(payload, null, 2));

    const safePayload = sanitizeTestHistoryPayload(payload);

    const { data, error } = await supabase
      .from("test_history")
      .insert([safePayload])
      .select("*")
      .single();

    if (error) {
      console.error("[EE_SAVE_COMPLETED_HISTORY_ERROR]", error);
      throw error;
    }

    console.log("[EE_SAVE_COMPLETED_HISTORY_SUCCESS]", JSON.stringify(data, null, 2));

    // Increment trial tests used for the user profile
    try {
      await profileService.incrementTrialTestsUsed(userData.user.id);
      // Track AI correction completion
      await activityService.trackUserActivity(
        "ee_corrected",
        "test",
        data?.id || "",
        { section: section || "Completo", score: normalized.totalScore }
      );
    } catch (err) {
      console.error("Failed to increment trial tests or track activity:", err);
    }

    this.emitHistoryUpdated();

    return data;
  },

  async saveWritingCorrectionAsNewHistoryRecord({
    correctionResult,
    originalWritingText = "",
    writingTasks = [],
    section = "Completo",
    timeUsed = null,
    wordCount = null,
  }: {
    correctionResult: any;
    originalWritingText?: string;
    writingTasks?: any[];
    section?: string;
    timeUsed?: number | string | null;
    wordCount?: number | null;
  }) {
    const normalized = normalizeWritingCorrection(correctionResult);

    if (!normalized.totalScore || normalized.totalScore <= 0) {
      console.error("[EE_MANUAL_SAVE_INVALID_SCORE]", normalized);
      throw new Error("Não foi possível salvar: pontuação inválida.");
    }

    const taskTexts = writingTasks
      .map((task: any) => task.userText || task.answer || task.text || "")
      .filter(Boolean);

    const fullOriginalText = originalWritingText || taskTexts.join("\n\n");

    const computedWordCount =
      wordCount ??
      String(fullOriginalText || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;

    const metadata = {
      isWritingTest: true,
      isFullWritingTest:
        String(section || "").toLowerCase().includes("completo") ||
        writingTasks.length >= 2,

      writingCorrection: normalized,
      originalWritingText,
      userText: originalWritingText,
      fullOriginalText,
      writingTasks,
      sectionScores: normalized.sectionScores || {
        sectionA: normalized.sectionA || null,
        sectionB: normalized.sectionB || null,
        total: {
          score: normalized.totalScore,
          maxScore: normalized.maxScore || 450,
        },
      },
      status: "concluido",
      totalScore: normalized.totalScore,
      maxScore: normalized.maxScore || 450,
      nclc: normalized.nclc,
      cefr: normalized.cefr,
      wordCount: computedWordCount,
      timeUsed,
      correctionSource: normalized.source || "ai",
      savedManually: true,
      correctionSavedAt: new Date().toISOString(),
    };


    const payload = {
      competence: "Expression Écrite (EE)",
      section: section || "Completo",
      score: normalized.totalScore,
      metadata,
    };

    // Authenticate user
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      throw new Error("Usuário não autenticado. Não é possível salvar histórico.");
    }

    (payload as any).user_id = userData.user.id;

    const safePayload = sanitizeTestHistoryPayload(payload);
    console.log("[EE_MANUAL_SAVE_SAFE_PAYLOAD]", JSON.stringify(safePayload, null, 2));

    console.log("[EE_MANUAL_SAVE_PAYLOAD_KEYS_FINAL]", Object.keys(safePayload));
    console.log("[EE_MANUAL_SAVE_PAYLOAD_FINAL]", JSON.stringify(safePayload, null, 2));

    const { data, error } = await supabase
      .from("test_history")
      .insert([safePayload])
      .select("*")
      .single();

    if (error) {
      console.error("[EE_MANUAL_SAVE_ERROR]", error);
      throw error;
    }

    console.log("[EE_MANUAL_SAVE_SUCCESS]", JSON.stringify(data, null, 2));

    // Increment trial tests used for the user profile
    try {
      await profileService.incrementTrialTestsUsed(userData.user.id);
      // Track AI correction completion
      await activityService.trackUserActivity(
        "ee_corrected",
        "test",
        data?.id || "",
        { section: section || "Completo", score: normalized.totalScore }
      );
    } catch (err) {
      console.error("Failed to increment trial tests or track activity:", err);
    }

    this.emitHistoryUpdated();

    return data;
  },

  async saveWritingV2CorrectionToHistory({
    task,
    correction,
    testMode,
  }: {
    task: any;
    correction: any;
    testMode?: "single" | "complete" | "history_review";
  }) {
    console.log("[EE_V2_HISTORY_SAVE_START]", {
      task,
      correction,
      testMode,
    });

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      console.error("[EE_V2_HISTORY_SESSION_ERROR]", sessionError);
    }

    const userId = sessionData?.session?.user?.id;

    console.log("[EE_V2_HISTORY_AUTH_STATE]", {
      hasSession: Boolean(sessionData?.session),
      userId,
    });

    if (!userId) {
      throw new Error("Usuário não autenticado. Não foi possível salvar no histórico.");
    }

    if (!task) {
      throw new Error("Não foi possível salvar: tarefa não encontrada.");
    }

    if (!correction) {
      throw new Error("Não foi possível salvar: correção não encontrada.");
    }

    const section = task.section || correction.section;

    if (section !== "Section A" && section !== "Section B") {
      throw new Error(`Seção inválida para EE v2: ${section}`);
    }

    const originalText = String(
      task.userText ||
      task.answer ||
      task.response ||
      correction.originalText ||
      ""
    ).trim();

    if (!originalText) {
      console.error("[EE_V2_HISTORY_EMPTY_TEXT]", {
        task,
        correction,
      });

      throw new Error("Não foi possível salvar: texto da redação está vazio.");
    }

    const wordCount =
      typeof task.wordCount === "number"
        ? task.wordCount
        : originalText.split(/\s+/).filter(Boolean).length;

    const minimumWordsRequired =
      typeof task.minimumWordsRequired === "number"
        ? task.minimumWordsRequired
        : section === "Section B"
          ? 200
          : 80;

    const metadata = {
      type: "writing_linguistic_correction",
      writingVersion: 2,
      isOfficialEvaluation: false,
      testMode: testMode || "single",

      section,

      writingTask: {
        ...task,
        section,
        userText: originalText,
        wordCount,
        minimumWordsRequired,
        meetsMinimumWordCount: wordCount >= minimumWordsRequired,
      },

      writingCorrection: correction,

      originalText,
      prompt: task.prompt || task.question || task.instruction || "",
      title: task.title || "",

      wordCount,
      minimumWordsRequired,
      meetsMinimumWordCount: wordCount >= minimumWordsRequired,

      errorCount: Array.isArray(correction.errors) ? correction.errors.length : 0,
      mainFocusAreas: correction.mainFocusAreas || [],

      savedAt: new Date().toISOString(),
    };

    const scoreVal = parseNumericValue(correction.estimatedScore ?? correction.score);
    const maxScoreVal = parseNumericValue(correction.maxScore ?? correction.max_score ?? 450);
    const nclcVal = correction.nclcEstimate || correction.nclcLevel || correction.nclc_level;
    const cefrVal = correction.cecrlEstimate || correction.cefrLevel || correction.cefr_level;
    const percentageVal = scoreVal !== null && maxScoreVal ? Math.round((scoreVal / maxScoreVal) * 100) : null;
    const attemptKeyVal = task.attemptKey || task.attempt_key || task.metadata?.testAttemptId || correction.attemptKey || correction.attempt_key;

    const payload: any = {
      user_id: userId,
      competence: "Expression Écrite (EE)",
      section,
      score: scoreVal,
      max_score: maxScoreVal,
      percentage: percentageVal,
      nclc_level: nclcVal,
      cefr_level: cefrVal,
      metadata,
      attempt_key: attemptKeyVal || null,
    };

    console.log("[EE_V2_HISTORY_PAYLOAD_FINAL]", JSON.stringify(payload, null, 2));

    let existingRecord = null;
    const historyRecordId = task.historyRecordId || task.history_record_id || task.id;

    if (historyRecordId && typeof historyRecordId === "string" && !historyRecordId.startsWith("h-") && !historyRecordId.startsWith("fe_") && !historyRecordId.startsWith("sw_") && !historyRecordId.startsWith("smc_")) {
      try {
        const { data } = await supabase
          .from("test_history")
          .select("*")
          .eq("id", historyRecordId)
          .eq("user_id", userId)
          .maybeSingle();
        if (data) existingRecord = data;
      } catch (err) {
        console.warn("[EE_V2_HISTORY_FIND_BY_ID_ERROR]", err);
      }
    }

    if (!existingRecord && attemptKeyVal && typeof attemptKeyVal === "string" && !attemptKeyVal.startsWith("h-")) {
      try {
        const { data } = await supabase
          .from("test_history")
          .select("*")
          .eq("attempt_key", attemptKeyVal)
          .eq("user_id", userId)
          .maybeSingle();
        if (data) existingRecord = data;
      } catch (err) {
        console.warn("[EE_V2_HISTORY_FIND_BY_ATTEMPT_KEY_ERROR]", err);
      }
    }

    let data = null;
    let error = null;

    if (existingRecord) {
      console.log("[EE_V2_HISTORY_UPDATE] Updating existing record", { id: existingRecord.id });
      const res = await supabase
        .from("test_history")
        .update(payload)
        .eq("id", existingRecord.id)
        .eq("user_id", userId)
        .select("*")
        .single();
      data = res.data;
      error = res.error;
    } else {
      console.log("[EE_V2_HISTORY_INSERT] Inserting new record");
      const res = await supabase
        .from("test_history")
        .insert([payload])
        .select("*")
        .single();
      data = res.data;
      error = res.error;
    }

    if (error) {
      console.error("[EE_V2_HISTORY_SUPABASE_SAVE_ERROR]", {
        error,
        payload,
      });

      throw new Error(error.message || "Erro ao salvar registro no histórico.");
    }

    if (!data?.id) {
      console.error("[EE_V2_HISTORY_NO_ID_RETURNED]", data);
      throw new Error("O Supabase não retornou ID do registro salvo.");
    }

    console.log("[EE_V2_HISTORY_SAVE_SUCCESS]", {
      id: data.id,
      section: data.section,
      metadataType: data.metadata?.type,
      metadataSection: data.metadata?.section,
    });

    // Increment trial tests used for the user profile
    try {
      await profileService.incrementTrialTestsUsed(userId);
      // Track AI correction completion
      await activityService.trackUserActivity(
        "ee_corrected",
        "test",
        data?.id || "",
        { section: section || "Completo", score: data?.score }
      );
    } catch (err) {
      console.error("Failed to increment trial tests or track activity:", err);
    }

    this.emitHistoryUpdated();

    return data;
  },

  // Generates unique id
  generateUniqueId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `h-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  },

  // Get test history rows
  async getTestHistory(): Promise<HistoryItem[]> {
    if (!isSupabaseConfigured || !supabase) {
      return this.getLocalHistory();
    }
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        console.warn("[HistoryService] No authenticated user found for getTestHistory. Returning empty.");
        return [];
      }

      const { data, error } = await supabase
        .from("test_history")
        .select("*")
        .eq("user_id", userData.user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []).map(mapSupabaseToHistoryItem);
    } catch (err) {
      console.error("Error fetching history from Supabase:", err);
      return this.getLocalHistory();
    }
  },

  // Get single history row
  async getTestHistoryById(id: string): Promise<HistoryItem | null> {
    if (!isSupabaseConfigured || !supabase) {
      const local = this.getLocalHistory();
      return local.find(item => item.id === id) || null;
    }
    try {
      if (id.startsWith("h-") || id.startsWith("fe_") || id.startsWith("sw_") || id.startsWith("smc_")) {
        const local = this.getLocalHistory();
        return local.find(item => item.id === id) || null;
      }
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        console.warn("[HistoryService] No authenticated user found for getTestHistoryById. Returning null.");
        return null;
      }

      const { data, error } = await supabase
        .from("test_history")
        .select("*")
        .eq("id", id)
        .eq("user_id", userData.user.id)
        .single();

      if (error) throw error;
      return data ? mapSupabaseToHistoryItem(data) : null;
    } catch (err) {
      console.error(`Error fetching history ID ${id} from Supabase:`, err);
      const local = this.getLocalHistory();
      return local.find(item => item.id === id) || null;
    }
  },

  // Create or Update test history row based on attempt_key
  async createOrUpdateTestHistory(record: any): Promise<HistoryItem> {
    streakService.registerActivity();
    
    // Ensure attempt_key exists
    let attemptKey = record.attempt_key || record.attemptKey || record.metadata?.testAttemptId;
    if (!attemptKey) {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        attemptKey = crypto.randomUUID();
      } else {
        attemptKey = `h-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      }
    }
    
    // Synchronize attempt_key across fields
    if (!record.metadata) record.metadata = {};
    record.attempt_key = attemptKey;
    record.attemptKey = attemptKey;
    record.metadata.testAttemptId = attemptKey;

    console.log("[HistoryService] saving", {
      attemptKey: attemptKey,
      testType: record.testType || record.test_type || "unknown",
      competence: record.competence || "Não informado"
    });

    if (!isSupabaseConfigured || !supabase) {
      const local = this.getLocalHistory();
      const existingIdx = local.findIndex(item => (item.metadata?.testAttemptId === attemptKey || (item as any).attempt_key === attemptKey));
      if (existingIdx !== -1) {
        const updated = { ...local[existingIdx], ...record };
        local[existingIdx] = updated;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(local));
        this.emitHistoryUpdated();
        console.log("[HistoryService] saved (local update)", { id: updated.id });
        return updated as HistoryItem;
      }
      const saved = this.createLocalHistory(record);
      console.log("[HistoryService] saved (local insert)", { id: saved.id });

      // Track first test completed for local mock history
      const freshLocal = this.getLocalHistory();
      if (freshLocal.length === 1) {
        activityService.trackUserActivity("first_test_completed", "test", saved.id, {
          competence: record.competence,
          timestamp: new Date().toISOString()
        });
      }

      return saved;
    }

    // Authenticate user
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      throw new Error("Usuário não autenticado. Não é possível salvar histórico.");
    }

    const payload = mapHistoryToSupabase({
      ...record,
      user_id: userData.user.id
    });

    console.log("[HistoryService] saving for user", {
      userId: userData.user.id,
      attemptKey,
      testType: payload.test_type,
      competence: payload.competence
    });

    try {
      console.log("[HistoryService] sanitized payload", {
        user_id: payload.user_id,
        score: payload.score,
        max_score: payload.max_score,
        percentage: payload.percentage,
        total_questions: payload.total_questions,
        correct_answers: payload.correct_answers,
        duration_seconds: payload.duration_seconds,
        attempt_key: payload.attempt_key
      });

      const { data: existingData, error: findError } = await supabase
        .from("test_history")
        .select("*")
        .eq("attempt_key", attemptKey)
        .eq("user_id", userData.user.id)
        .maybeSingle();

      if (findError) {
        throw findError;
      }

      let savedData = null;
      const safePayload = sanitizeTestHistoryPayload(payload);

      if (existingData) {
        const { data, error } = await supabase
          .from("test_history")
          .update(safePayload)
          .eq("id", existingData.id)
          .eq("user_id", userData.user.id)
          .select()
          .single();
        if (error) throw error;
        savedData = data;
      } else {
        if (safePayload.id) delete (safePayload as any).id;
        const { data, error } = await supabase
          .from("test_history")
          .insert([safePayload])
          .select()
          .single();
        if (error) throw error;
        savedData = data;

        // Increment trial tests used for the user profile upon completing a new test
        try {
          await profileService.incrementTrialTestsUsed(userData.user.id);
          // Track test completion
          await activityService.trackUserActivity(
            "test_completed",
            "test",
            savedData?.id || attemptKey,
            { competence: payload.competence, test_type: payload.test_type }
          );

          // Check if this is their first completed test
          const history = await this.getTestHistory();
          if (history.length === 1) { // since we just inserted one record
            await activityService.trackUserActivity("first_test_completed", "test", savedData?.id || attemptKey, {
              competence: payload.competence,
              test_type: payload.test_type,
              timestamp: new Date().toISOString()
            });
          }
        } catch (err) {
          console.error("Failed to increment trial tests or track activity:", err);
        }
      }

      this.emitHistoryUpdated();
      const mapped = mapSupabaseToHistoryItem(savedData);
      console.log("[EE_CORRECTION_SAVE_SUCCESS]", {
        id: savedData?.id,
        attemptKey: savedData?.attempt_key,
        savedMetadata: savedData?.metadata
      });
      return mapped;
    } catch (error: any) {
      console.error("[EE_CORRECTION_SAVE_ERROR]", error);
      const mappedError = handleSupabaseError(error, "history");

      // Fail-safe backup locally so the student does not lose their score
      try {
        const local = this.getLocalHistory();
        const existingIdx = local.findIndex(item => (item.metadata?.testAttemptId === attemptKey || (item as any).attempt_key === attemptKey));
        let updatedRecordItem: any = null;
        if (existingIdx !== -1) {
          updatedRecordItem = { ...local[existingIdx], ...record };
          local[existingIdx] = updatedRecordItem;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(local));
        } else {
          updatedRecordItem = this.createLocalHistory(record);
        }
        this.emitHistoryUpdated();
        console.log("[HistoryService] Fail-safe local backup saved due to database failure:", { id: updatedRecordItem?.id });
      } catch (backupError) {
        console.error("[HistoryService] Failed to save local backup", backupError);
      }
      
      // Propagate mapped error cleanly so caller is aware of DB failure
      throw mappedError;
    }
  },

  // Payload formatting utility for different competence types
  buildTestHistoryPayload(testContext: any): any {
    const attemptId = testContext.attemptKey || testContext.attempt_key || testContext.metadata?.testAttemptId || this.generateUniqueId();
    const completedAt = testContext.completedAt || new Date().toISOString();
    
    console.log("[HistoryService] buildPayload", {
      attemptId,
      competence: testContext.competence,
      hasCorrection: !!(testContext.writingCorrection || testContext.aiCorrection)
    });
    
    // A) Compréhension Écrite (CE)
    if (testContext.competence === "Reading" || testContext.competence === "Compréhension Écrite (CE)") {
      const scoreVal = testContext.score ?? 0;
      const totalQ = testContext.totalQuestions ?? testContext.maxScore ?? 0;
      const pct = testContext.percentage ?? (totalQ > 0 ? Math.round((scoreVal / totalQ) * 100) : 0);
      
      return {
        testType: testContext.testType || "competence",
        competence: "Compréhension Écrite (CE)",
        section: testContext.sectionName || testContext.section || "Geral",
        score: scoreVal,
        maxScore: totalQ,
        percentage: pct,
        nclcLevel: testContext.nclcLevel || null,
        cefrLevel: testContext.cefrLevel || null,
        totalQuestions: totalQ,
        correctAnswers: scoreVal,
        durationSeconds: testContext.durationSeconds || 0,
        answers: testContext.answers || {},
        status: "concluido",
        result: testContext.result || {
          score: scoreVal,
          maxScore: totalQ,
          percentage: pct,
          nclcLevel: testContext.nclcLevel || null,
          cefrLevel: testContext.cefrLevel || null,
          correctAnswers: scoreVal,
          totalQuestions: totalQ
        },
        metadata: {
          testAttemptId: attemptId,
          completedAt,
          hasCE: true,
          hasCO: false,
          hasEE: false,
          hasAiCorrection: false,
          source: "supabase",
          ...(testContext.metadata || {})
        },
        attemptKey: attemptId
      };
    }

    // B) Compréhension Orale (CO)
    if (testContext.competence === "Listening" || testContext.competence === "Compréhension Orale (CO)") {
      const scoreVal = testContext.score ?? 0;
      const totalQ = testContext.totalQuestions ?? testContext.maxScore ?? 0;
      const pct = testContext.percentage ?? (totalQ > 0 ? Math.round((scoreVal / totalQ) * 100) : 0);
      
      return {
        testType: testContext.testType || "competence",
        competence: "Compréhension Orale (CO)",
        section: testContext.sectionName || testContext.section || "Geral",
        score: scoreVal,
        maxScore: totalQ,
        percentage: pct,
        nclcLevel: testContext.nclcLevel || null,
        cefrLevel: testContext.cefrLevel || null,
        totalQuestions: totalQ,
        correctAnswers: scoreVal,
        durationSeconds: testContext.durationSeconds || 0,
        answers: testContext.answers || {},
        status: "concluido",
        result: testContext.result || {
          score: scoreVal,
          maxScore: totalQ,
          percentage: pct,
          nclcLevel: testContext.nclcLevel || null,
          cefrLevel: testContext.cefrLevel || null,
          correctAnswers: scoreVal,
          totalQuestions: totalQ
        },
        metadata: {
          testAttemptId: attemptId,
          completedAt,
          hasCE: false,
          hasCO: true,
          hasEE: false,
          hasAiCorrection: false,
          source: "supabase",
          ...(testContext.metadata || {})
        },
        attemptKey: attemptId
      };
    }

    // C) Expression Écrite (EE)
    if (isWritingCompetency(testContext.competence)) {
      const isCorrected = testContext.result?.ee?.status === "corrected" || 
                        testContext.status === "Corrigido por IA" ||
                        !!testContext.writingCorrection ||
                        !!testContext.aiCorrection ||
                        !!testContext.correction;
      
      const totalWords = (testContext.answers?.sectionA?.wordCount || 0) + 
                        (testContext.answers?.sectionB?.wordCount || 0) || 
                        testContext.words || 
                        testContext.result?.ee?.wordCount ||
                        0;

      const corr = testContext.writingCorrection || 
                   testContext.aiCorrection || 
                   testContext.correction ||
                   testContext.result?.ee?.correction;
      
      const score = parseNumericValue(
        corr?.estimatedScore ?? 
        corr?.score ?? 
        testContext.score ??
        testContext.result?.ee?.score
      );
      
      const levels = getTefLevelByScore("ee", score);
      const nclc = corr?.nclcEstimate ?? corr?.nclcLevel ?? testContext.nclcLevel ?? levels?.nclc;
      const cefr = corr?.cecrlEstimate ?? corr?.cefrLevel ?? testContext.cefrLevel ?? levels?.cecrl;
      
      const isFullEE = isFullWritingSection(testContext.sectionName || testContext.section);

      return {
        testType: isFullEE ? "complete" : (testContext.testType || "competence"),
        competence: "Expression Écrite (EE)",
        section: testContext.sectionName || testContext.section || "Expression Écrite",
        score: isCorrected ? score : null,
        maxScore: 450,
        percentage: isCorrected ? Math.round(((score || 0) / 450) * 100) : null,
        nclcLevel: isCorrected ? (nclc || null) : null,
        cefrLevel: isCorrected ? (cefr || null) : null,
        totalQuestions: isFullEE ? 2 : 1,
        correctAnswers: null,
        words: totalWords,
        durationSeconds: testContext.durationSeconds || testContext.timeUsed || 0,
        answers: testContext.answers || {},
        status: isCorrected ? "concluido" : "correction_pending",
        result: testContext.result || {
          ee: {
            status: isCorrected ? "corrected" : "pending",
            score: isCorrected ? score : null,
            maxScore: 450,
            nclcLevel: isCorrected ? nclc : null,
            cefrLevel: isCorrected ? cefr : null,
            wordCount: totalWords,
            correction: corr || null
          }
        },
        metadata: {
          testAttemptId: attemptId,
          completedAt,
          hasCE: false,
          hasCO: false,
          hasEE: true,
          hasAiCorrection: isCorrected,
          isWritingTest: true,
          isFullWritingTest: isFullEE,
          writingCorrection: corr || null,
          fullOriginalText: testContext.text || "",
          originalWritingText: testContext.text || "",
          userText: testContext.text || "",
          writingTasks: [
             ...(testContext.answers?.sectionA ? [testContext.answers.sectionA] : []),
             ...(testContext.answers?.sectionB ? [testContext.answers.sectionB] : [])
          ],
          nclc: nclc,
          cefr: cefr,
          totalScore: score,
          source: "supabase",
          ...(testContext.metadata || {})
        },
        attemptKey: attemptId
      };
    }

    // D) Simulado completo (Full Exam)
    const ce = testContext.result?.ce || {};
    const co = testContext.result?.co || {};
    const ee = testContext.result?.ee || {};
    
    const ceQuestionsCount = ce.totalQuestions || ce.total || 0;
    const coQuestionsCount = co.totalQuestions || co.total || 0;
    const ceCorrect = ce.correctAnswers || ce.correct || 0;
    const coCorrect = co.correctAnswers || co.correct || 0;
    
    const totalQuestions = ceQuestionsCount + coQuestionsCount;
    const correctAnswers = ceCorrect + coCorrect;
    const totalPercentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : null;
    
    return {
      testType: "complete",
      competence: "Simulado Completo",
      section: "Completo",
      score: parseNumericValue(testContext.score) || correctAnswers || null,
      maxScore: totalQuestions || null,
      percentage: totalPercentage,
      nclcLevel: null,
      cefrLevel: null,
      totalQuestions,
      correctAnswers,
      durationSeconds: testContext.durationSeconds || 0,
      answers: testContext.answers || {},
      result: {
        ce: {
          score: ceCorrect,
          maxScore: ceQuestionsCount,
          percentage: ce.percentage ?? (ceQuestionsCount > 0 ? Math.round((ceCorrect / ceQuestionsCount) * 100) : 0),
          nclcLevel: ce.nclcLevel || null,
          cefrLevel: ce.cefrLevel || null,
          correctAnswers: ceCorrect,
          totalQuestions: ceQuestionsCount,
          answerReview: ce.answerReview || []
        },
        co: {
          score: coCorrect,
          maxScore: coQuestionsCount,
          percentage: co.percentage ?? (coQuestionsCount > 0 ? Math.round((coCorrect / coQuestionsCount) * 100) : 0),
          nclcLevel: co.nclcLevel || null,
          cefrLevel: co.cefrLevel || null,
          correctAnswers: coCorrect,
          totalQuestions: coQuestionsCount,
          answerReview: co.answerReview || []
        },
        ee: {
          status: ee.status || "pending",
          score: parseNumericValue(ee.score) || null,
          maxScore: 450,
          nclcLevel: ee.nclcLevel || null,
          cefrLevel: ee.cefrLevel || null,
          wordCount: ee.wordCount || 0,
          correction: ee.correction || null
        },
        total: {
          score: correctAnswers,
          maxScore: totalQuestions,
          percentage: totalPercentage
        }
      },
      metadata: {
        testAttemptId: attemptId,
        completedAt,
        hasCE: ceQuestionsCount > 0,
        hasCO: coQuestionsCount > 0,
        hasEE: true,
        hasAiCorrection: false,
        source: "supabase",
        ...(testContext.metadata || {})
      },
      attemptKey: attemptId
    };
  },
  // Create test history row
  async createTestHistory(record: Omit<HistoryItem, "id"> & { id?: string }): Promise<HistoryItem> {
    return this.createOrUpdateTestHistory(record);
  },

  // Update test history row
  async updateTestHistory(id: string, record: Partial<HistoryItem>): Promise<HistoryItem> {
    if (!isSupabaseConfigured || !supabase || id.startsWith("h-") || id.startsWith("fe_") || id.startsWith("sw_") || id.startsWith("smc_")) {
      return this.updateLocalHistory(id, record);
    }
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("Usuário não autenticado. Não é possível atualizar histórico.");
      }

      const existing = await this.getTestHistoryById(id);
      const mergedRecord = existing ? { ...existing, ...record } : record;
      
      const row = mapHistoryToSupabase({
        ...mergedRecord,
        user_id: userData.user.id
      });
      if (row.id) delete row.id;
      
      const { data, error } = await supabase
        .from("test_history")
        .update(row)
        .eq("id", id)
        .eq("user_id", userData.user.id)
        .select()
        .single();

      if (error) throw error;
      this.emitHistoryUpdated();
      return mapSupabaseToHistoryItem(data);
    } catch (err) {
      const mappedError = handleSupabaseError(err, "history");
      console.error(`Error updating history ID ${id} in Supabase:`, mappedError);
      return this.updateLocalHistory(id, record);
    }
  },
  
  /**
   * Specialized update for Expression Écrite corrections
   */
  async updateWritingCorrection(id: string, correctionResult: any, writingTasks: any[] = []): Promise<HistoryItem> {
    const normalized = normalizeWritingCorrection(correctionResult);
    
    console.log("[EE_CORRECTION_SAVE_ATTEMPT]", {
      historyId: id,
      correctionResult,
      normalized
    });

    try {
      const current = await this.getHistoryById(id);
      if (!current) throw new Error(`Registro de histórico ${id} não encontrado.`);

      const metadata = {
        ...(current.metadata || {}),
        isWritingTest: true,
        writingCorrection: normalized,
        writingTasks: writingTasks.length > 0 ? writingTasks : (current.metadata?.writingTasks || []),
        sectionScores: correctionResult.sectionScores || {
          sectionA: normalized.sectionA || null,
          sectionB: normalized.sectionB || null,
          total: {
            score: normalized.score,
            maxScore: normalized.maxScore || 450,
          },
        },
        nclc: normalized.nclc,
        cefr: normalized.cefr,
        totalScore: normalized.score,
        maxScore: normalized.maxScore,
        correctionSavedAt: new Date().toISOString()
      };

      const updatePayload = {
        ...current,
        score: normalized.score,
        maxScore: normalized.maxScore,
        nclcLevel: normalized.nclc,
        cefrLevel: normalized.cefr,
        status: "Corrigido por IA",
        metadata
      };

      console.log("[EE_CORRECTION_HISTORY_UPDATE_PAYLOAD]", JSON.stringify(updatePayload, null, 2));

      const saved = await this.createOrUpdateTestHistory(updatePayload);
      
      console.log("[EE_CORRECTION_HISTORY_UPDATE_SAVED]", JSON.stringify(saved, null, 2));

      return saved;
    } catch (err) {
      console.error("[EE_CORRECTION_SAVE_ERROR]", err);
      throw err;
    }
  },

  async persistWritingCorrection({
    historyId,
    testResultId,
    correctionResult,
    writingTasks,
    section,
    competency,
    existingMetadata,
  }: {
    historyId?: string;
    testResultId?: string;
    correctionResult: any;
    writingTasks?: any[];
    section?: string;
    competency?: string;
    existingMetadata?: any;
  }): Promise<HistoryItem> {
    const normalized = normalizeWritingCorrection(correctionResult);

    const id = historyId || testResultId;

    if (!id) {
      console.error("[persistWritingCorrection] Missing historyId/testResultId");
      throw new Error("Não foi possível salvar a correção: ID do histórico ausente.");
    }

    const previousMetadata = existingMetadata || {};

    const metadata = {
      ...previousMetadata,
      isWritingTest: true,
      isFullWritingTest:
        String(section || "").toLowerCase().includes("teste completo") ||
        (Array.isArray(writingTasks) && writingTasks.length >= 2),

      writingCorrection: normalized,
      writingTasks: writingTasks || previousMetadata.writingTasks || [],
      sectionScores: normalized.sectionScores || previousMetadata.sectionScores || {
        sectionA: normalized.sectionA || null,
        sectionB: normalized.sectionB || null,
        total: {
          score: normalized.totalScore,
          maxScore: normalized.maxScore || 450,
        },
      },

      nclc: normalized.nclc,
      cefr: normalized.cefr,
      totalScore: normalized.totalScore,
      maxScore: normalized.maxScore || 450,
      correctionSavedAt: new Date().toISOString(),
    };

    const payload = {
      score: normalized.totalScore,
      max_score: normalized.maxScore || 450,
      nclc_level: normalized.nclc,
      cefr_level: normalized.cefr,
      status: "concluido",
      metadata,
    };

    console.log("[PERSIST_WRITING_CORRECTION_PAYLOAD]", JSON.stringify({
      id,
      payload,
    }, null, 2));

    if (!isSupabaseConfigured || !supabase || id.startsWith("h-") || id.startsWith("fe_") || id.startsWith("sw_") || id.startsWith("smc_")) {
      console.log("[persistWritingCorrection] Fallback to local history");
      return this.updateLocalHistory(id, payload);
    }

    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      throw new Error("Usuário não autenticado. Não é possível salvar histórico.");
    }

    const { data, error } = await supabase
      .from("test_history")
      .update(payload)
      .eq("id", id)
      .eq("user_id", userData.user.id)
      .select("*")
      .single();

    if (error) {
      console.error("[PERSIST_WRITING_CORRECTION_ERROR]", error);
      throw error;
    }

    console.log("[PERSIST_WRITING_CORRECTION_SUCCESS]", JSON.stringify(data, null, 2));

    this.emitHistoryUpdated();
    return mapSupabaseToHistoryItem(data);
  },

  // Delete single test history row
  async deleteTestHistory(id: string): Promise<boolean> {
    if (!isSupabaseConfigured || !supabase) {
      this.deleteLocalHistory(id);
      return true;
    }
    try {
      if (id.startsWith("h-") || id.startsWith("fe_") || id.startsWith("sw_") || id.startsWith("smc_")) {
        this.deleteLocalHistory(id);
        return true;
      }
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("Usuário não autenticado. Não é possível deletar histórico.");
      }

      const { error } = await supabase
        .from("test_history")
        .delete()
        .eq("id", id)
        .eq("user_id", userData.user.id);

      if (error) throw error;
      this.emitHistoryUpdated();
      return true;
    } catch (err) {
      const mappedError = handleSupabaseError(err, "history");
      console.error(`Error deleting history ID ${id} from Supabase:`, mappedError);
      this.deleteLocalHistory(id);
      return true;
    }
  },

  // --- COMPATIBILITY WRAPPERS FOR OLD PAGES ---
  getHistoryItems(): HistoryItem[] {
    return this.getLocalHistory();
  },

  saveHistoryItem(item: Omit<HistoryItem, "id"> & { id?: string }): HistoryItem {
    this.createTestHistory(item).catch(err => {
      console.error("Async save history item failed:", err);
    });
    return { ...item, id: item.id || this.generateUniqueId() } as HistoryItem;
  },

  deleteHistoryItem(id: string): void {
    this.deleteTestHistory(id).catch(err => {
      console.error("Async delete history item failed:", err);
    });
  },

  deleteHistoryItems(ids: string[]): void {
    ids.forEach(id => {
      this.deleteTestHistory(id).catch(err => {
        console.error("Async delete history items failed:", err);
      });
    });
  },

  async clearHistory(): Promise<void> {
    if (isSupabaseConfigured && supabase) {
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        if (userData?.user) {
          const { error } = await supabase
            .from("test_history")
            .delete()
            .eq("user_id", userData.user.id);
          if (error) throw error;
        }
      } catch (err) {
        const mappedError = handleSupabaseError(err, "history");
        console.error("Supabase history clear failed:", mappedError);
      }
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    this.emitHistoryUpdated();
  },

  // --- LOCAL FALLBACK METHODS ---
  getLocalHistory(): HistoryItem[] {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      const mappedMock: HistoryItem[] = historyData.map(h => ({
        id: h.id || this.generateUniqueId(),
        date: h.date,
        competence: h.competence,
        score: h.score,
        timeSpent: h.timeSpent,
        status: h.status,
        words: h.words,
        mode: "single",
        selectedSection: (h as any).selectedSection,
        metadata: {
          testAttemptId: "mock-" + h.id
        }
      }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mappedMock));
      return mappedMock;
    }
    try {
      const items = JSON.parse(raw);
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  },

  createLocalHistory(record: Omit<HistoryItem, "id"> & { id?: string }): HistoryItem {
    const items = this.getLocalHistory();
    const newItem: HistoryItem = {
      ...record,
      id: record.id || this.generateUniqueId()
    } as HistoryItem;
    items.unshift(newItem);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    this.emitHistoryUpdated();
    return newItem;
  },

  updateLocalHistory(id: string, partial: Partial<HistoryItem>): HistoryItem {
    const items = this.getLocalHistory();
    const idx = items.findIndex(item => item.id === id);
    if (idx === -1) {
      throw new Error(`Local history ID ${id} not found.`);
    }
    const updated = { ...items[idx], ...partial };
    items[idx] = updated;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    this.emitHistoryUpdated();
    return updated;
  },

  deleteLocalHistory(id: string): void {
    const items = this.getLocalHistory();
    const filtered = items.filter(item => item.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    this.emitHistoryUpdated();
  }
};

// Quando Supabase Auth for implementado, substituir policies públicas por policies baseadas em auth.uid().
